// base
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// hooks
import { usePaginationV2, type Pagination } from './usePaginationV2';

// models
import type { CommonQueryOptions, BaseApiResponse } from 'modules';

// libraries
import { type QueryKey, useQuery, type UseQueryResult } from '@tanstack/react-query';

/**
 * `useCommonQuery` 훅의 매개변수를 정의하는 인터페이스 입니다.
 *
 * @template TReqData - 요청 데이터 타입
 * @template TResData - 응답 데이터 타입, , `BaseApiResponse`을 확장해야 합니다.
 * @template TError - 에러 타입
 */
export interface UseCommonQueryParams<
  TReqData,
  TResData extends BaseApiResponse,
  TError,
  TSelectData = TResData
> {
  /** 요청에 사용할 데이터 */
  data: TReqData;

  /**
   * `useQuery`에 전달할 옵션입니다. `queryKey`와 `queryFn`은 제외됩니다.
   * @see {@link UseQueryOptions}
   */
  options?: CommonQueryOptions<TResData, TError, TSelectData>;

  /**
   * 쿼리 키를 생성하는 함수입니다.
   *
   * @param data - 요청 데이터
   * @returns 쿼리 키 배열
   */
  queryKeyFactory: (data: TReqData) => QueryKey;

  /** 데이터를 가져오는 쿼리 함수 */
  queryFn: (data: TReqData, pagination: Pagination) => Promise<TResData>;
}

/**
 * `useCommonQuery` 훅의 반환 타입입니다.
 *
 * @template TResData - 응답 데이터의 타입
 * @template TError - 에러의 타입
 */
export type UseCommonQueryResult<TResData, TError> = UseQueryResult<TResData, TError> & {
  /** 총 데이터 수 */
  totalElement: number;
  /** 페이지네이션 정보 */
  pagination: Pagination;
  /** 페이지 크기를 변경하는 함수 */
  onChangePageSize: (page: number, pageSize?: number) => void;
  /** 성공시 호출되는 함수 */
  onSuccess: () => void;
  /** 실패시 호출되는 함수 */
  onError: () => void;
};

/**
 * 공통 쿼리 훅으로, 페이지네이션을 지원하는 데이터 페칭 로직을 제공합니다.
 *
 * @template TReqData - 요청 데이터의 타입
 * @template TResData - 응답 데이터의 타입, `BaseApiResponse`을 확장해야 합니다.
 * @template TError - 에러의 타입
 *
 * @param {UseCommonQueryParams<TReqData, TResData, TError>} params - 훅의 매개변수들
 * @returns {UseCommonQueryResult<TResData, TError>} 쿼리 결과와 페이지네이션 정보를 포함한 객체
 *
 * @example
 * const { data, isLoading, pagination, onChangePageSize } = useCommonQuery({
 *   data: requestData,
 *   queryKeyFactory: (data) => ['key', data],
 *   queryFn: fetchFunction,
 * });
 */
export const useCommonQuery = <
  TReqData,
  TResData extends BaseApiResponse,
  TError,
  TSelectData = TResData
>(
  props: UseCommonQueryParams<TReqData, TResData, TError, TSelectData>
): UseCommonQueryResult<TSelectData, TError> => {
  const { data, options, queryKeyFactory, queryFn } = props;

  /** 원본 파라미터 데이터 */
  const originalParamsRef = useRef<TReqData | null>(null);

  /** 원본 데이터 */
  const originalDataRef = useRef<TResData | null>(null);

  /** 총 데이터 수 상태 관리 */
  const [totalEl, setTotalEl] = useState(0);

  /** 성공 핸들링 상태 */
  const prevSuccessRef = useRef<TSelectData | null>(null);

  /** 에러 핸들링 상태 */
  const prevErrorRef = useRef<TError | null>(null);

  /** 페이지네이션 훅 사용 */
  const { pagination, onChangePageSize } = usePaginationV2({
    totalElement: totalEl
  });

  /** 쿼리 키 생성 */
  const queryKey = useMemo(() => {
    const keyFactory = queryKeyFactory(data);

    const result = [...keyFactory, pagination.current, pagination.pageSize];

    return result;
  }, [data, pagination, queryKeyFactory]);

  const customQueryFn = useCallback(
    (data: TReqData, pagination: Pagination) => {
      let copiedPagination = {
        ...pagination
      };

      const currentParams = JSON.stringify(data);
      const prevParams = JSON.stringify(originalParamsRef.current);

      if (currentParams !== prevParams) {
        copiedPagination = {
          ...copiedPagination,
          current: 0,
          pageSize: pagination.pageSize
        };

        onChangePageSize(0, pagination.pageSize);
      }

      originalParamsRef.current = {
        ...data
      };

      return queryFn(data, copiedPagination);
    },
    [queryFn]
  );

  /** 데이터 페칭을 위한 `useQuery` 사용 */
  const queryResult = useQuery<TResData, TError, TSelectData>({
    queryKey,
    queryFn: () => customQueryFn(data, pagination),
    ...options,
    select: (data) => {
      originalDataRef.current = data;

      return options?.select ? options.select(data) : (data as unknown as TSelectData);
    }
  });

  const onSuccess = useCallback(() => {
    if (!options?.afterSuccess || !queryResult.isSuccess) return;

    options.afterSuccess(queryResult.data as TSelectData);
  }, [options, queryResult.data, queryResult.isSuccess]);

  const onError = useCallback(() => {
    if (!options?.afterError || !queryResult.isError) return;

    options.afterError(queryResult.error as TError);
  }, [options, queryResult.error, queryResult.isError]);

  /** 데이터 변경 시 총 데이터 수 업데이트 */
  useEffect(() => {
    const originalData = originalDataRef.current;

    if (!originalData?.totalMap) return;

    setTotalEl(originalData?.totalMap?.totalElements || 0);
  }, [queryResult.data]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data && queryResult.isFetched) {
      if (queryResult.data !== prevSuccessRef.current) {
        onSuccess();
        prevSuccessRef.current = queryResult.data;
      }
    } else {
      prevSuccessRef.current = null;
    }
  }, [queryResult.isSuccess, queryResult.isFetched, queryResult.data, onSuccess]);

  useEffect(() => {
    if (queryResult.isError && queryResult.error && queryResult.isFetched) {
      // 에러가 이전 에러와 다르면 onError()
      if (queryResult.error !== prevErrorRef.current) {
        onError();
        prevErrorRef.current = queryResult.error;
      }
    } else {
      // 에러가 해소되거나 없으면 초기화
      prevErrorRef.current = null;
    }
  }, [queryResult.isError, queryResult.isFetched, queryResult.error, onError]);

  /** 쿼리 결과와 페이지네이션 정보를 반환 */
  return {
    ...queryResult,
    totalElement: totalEl,
    pagination,
    onChangePageSize,
    onSuccess,
    onError
  };
};
